export default function FormattingTestPage() {
  return (
    <div className="min-h-screen px-6 pt-32 pb-20 lg:px-12 lg:pt-40 lg:pb-32">
      <div className="mx-auto max-w-5xl">
        {/* ── Header Spacing Test ── */}
        <section className="mb-16 border-b border-warm-stone/30 pb-16">
          <h1 className="mb-6">Formatting Test Page</h1>
          <p className="mb-4">
            This page tests that all HTML elements render correctly with proper
            spacing, no overlapping, and correct Tailwind styling. If anything
            looks broken here, there's a CSS issue to fix.
          </p>
          <p>
            The header above should have clear space between it and the browser
            URL bar. This paragraph should not overlap with anything.
          </p>
        </section>

        {/* ── Typography Test ── */}
        <section className="mb-16 border-b border-warm-stone/30 pb-16">
          <h2 className="mb-8">Typography</h2>

          <div className="space-y-6">
            <div>
              <h1>Heading 1 — The Largest</h1>
              <p>Paragraph after h1. There should be no overlap.</p>
            </div>

            <div>
              <h2>Heading 2 — Section Level</h2>
              <p>Paragraph after h2. There should be no overlap.</p>
            </div>

            <div>
              <h3>Heading 3 — Subsection</h3>
              <p>Paragraph after h3. There should be no overlap.</p>
            </div>

            <div>
              <h4 className="font-serif text-xl font-light">Heading 4 — Detail</h4>
              <p>Paragraph after h4. There should be no overlap.</p>
            </div>

            <div>
              <p className="font-body text-lg leading-relaxed">
                This is body text in the Lora font. It should have comfortable
                line height and be easy to read. Lorem ipsum dolor sit amet,
                consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut
                labore et dolore magna aliqua. Ut enim ad minim veniam, quis
                nostrud exercitation ullamco laboris.
              </p>
            </div>

            <div>
              <p className="font-script text-3xl text-faded-terracotta">
                This is script text (Italianno)
              </p>
              <p>Normal paragraph right after script text — no overlap expected.</p>
            </div>
          </div>
        </section>

        {/* ── Spacing / Margin Test ── */}
        <section className="mb-16 border-b border-warm-stone/30 pb-16">
          <h2 className="mb-8">Spacing &amp; Margins</h2>

          <div className="mb-8">
            <h3 className="mb-4">Tailwind margin utilities</h3>
            <div className="flex flex-wrap gap-4">
              <div className="rounded bg-antique-linen p-4">
                <code>mb-2</code>
              </div>
              <div className="rounded bg-antique-linen p-4">
                <code>mb-4</code>
              </div>
              <div className="rounded bg-antique-linen p-4">
                <code>p-6</code>
              </div>
              <div className="rounded bg-antique-linen p-4">
                <code>gap-4</code>
              </div>
            </div>
            <p className="mt-4">
              The boxes above should have equal internal padding and spacing between them.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="mb-4">Padding test boxes</h3>
            <div className="space-y-4">
              <div className="bg-cream p-2 ring-1 ring-warm-stone/20">
                <code>p-2</code> — 0.5rem padding
              </div>
              <div className="bg-cream p-4 ring-1 ring-warm-stone/20">
                <code>p-4</code> — 1rem padding
              </div>
              <div className="bg-cream p-6 ring-1 ring-warm-stone/20">
                <code>p-6</code> — 1.5rem padding
              </div>
              <div className="bg-cream p-8 ring-1 ring-warm-stone/20">
                <code>p-8</code> — 2rem padding
              </div>
            </div>
          </div>
        </section>

        {/* ── Lists Test ── */}
        <section className="mb-16 border-b border-warm-stone/30 pb-16">
          <h2 className="mb-8">Lists</h2>

          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h3 className="mb-4">Unordered List (default)</h3>
              <ul>
                <li>First item — should have a bullet</li>
                <li>Second item — should be spaced from first</li>
                <li>Third item with longer text that might wrap to a second line to test line spacing</li>
                <li>Fourth item</li>
              </ul>
            </div>

            <div>
              <h3 className="mb-4">Ordered List (default)</h3>
              <ol>
                <li>First numbered item</li>
                <li>Second numbered item</li>
                <li>Third numbered item with longer text that wraps</li>
                <li>Fourth numbered item</li>
              </ol>
            </div>

            <div>
              <h3 className="mb-4">Unordered List (with Tailwind)</h3>
              <ul className="list-disc space-y-2 pl-6">
                <li>First item with disc marker</li>
                <li>Second item with disc marker</li>
                <li>Third item with longer text to test wrap behavior</li>
                <li>Fourth item</li>
              </ul>
            </div>

            <div>
              <h3 className="mb-4">Ordered List (with Tailwind)</h3>
              <ol className="list-decimal space-y-2 pl-6">
                <li>First numbered item</li>
                <li>Second numbered item</li>
                <li>Third numbered item with longer text</li>
                <li>Fourth numbered item</li>
              </ol>
            </div>
          </div>
        </section>

        {/* ── Width / Centering Test ── */}
        <section className="mb-16 border-b border-warm-stone/30 pb-16">
          <h2 className="mb-8">Width &amp; Centering</h2>

          <div className="space-y-6">
            <div className="bg-cream/50 p-4 ring-1 ring-warm-stone/20">
              <p className="text-sm text-warm-stone">Full width (no max-w constraint)</p>
              <p>
                This paragraph should stretch to fill the entire container width.
                No whitespace should appear only on the right side.
              </p>
            </div>

            <div className="mx-auto max-w-3xl bg-cream/50 p-4 text-center ring-1 ring-warm-stone/20">
              <p className="text-sm text-warm-stone">max-w-3xl centered</p>
              <p>
                This container is max-w-3xl (768px) and centered. The text is
                centered. Equal whitespace on both sides.
              </p>
            </div>

            <div className="mx-auto max-w-2xl bg-cream/50 p-4 text-center ring-1 ring-warm-stone/20">
              <p className="text-sm text-warm-stone">max-w-2xl centered</p>
              <p>
                This container is max-w-2xl (672px) and centered.
              </p>
            </div>

            <div className="mx-auto max-w-xl bg-cream/50 p-4 text-center ring-1 ring-warm-stone/20">
              <p className="text-sm text-warm-stone">max-w-xl centered</p>
              <p>
                This container is max-w-xl (576px) and centered.
              </p>
            </div>
          </div>
        </section>

        {/* ── Grid Layout Test ── */}
        <section className="mb-16 border-b border-warm-stone/30 pb-16">
          <h2 className="mb-8">Grid Layout</h2>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="rounded-sm bg-antique-linen p-6 ring-1 ring-warm-stone/20"
              >
                <h3 className="mb-2 font-serif text-xl font-light">Card {n}</h3>
                <p className="text-sm text-soft-charcoal/70">
                  Each card should have equal width within the grid and proper
                  internal spacing.
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Button / Link Test ── */}
        <section className="mb-16 border-b border-warm-stone/30 pb-16">
          <h2 className="mb-8">Buttons &amp; Links</h2>

          <div className="flex flex-wrap items-center gap-6">
            <a href="#" className="text-faded-terracotta underline">
              Inline link
            </a>
            <button
              type="button"
              className="rounded-sm border border-soft-charcoal px-8 py-3 text-xs tracking-widest uppercase transition-colors hover:bg-soft-charcoal hover:text-parchment"
            >
              Outlined Button
            </button>
            <button
              type="button"
              className="rounded-sm bg-faded-terracotta px-8 py-3 text-xs tracking-widest uppercase text-parchment transition-colors hover:bg-faded-terracotta/90"
            >
              Filled Button
            </button>
          </div>
        </section>

        {/* ── Form Elements Test ── */}
        <section className="mb-16">
          <h2 className="mb-8">Form Elements</h2>

          <div className="mx-auto max-w-lg space-y-6">
            <div>
              <label className="mb-2 block font-serif text-sm tracking-wider uppercase text-warm-stone">
                Text Input
              </label>
              <input
                type="text"
                placeholder="Type here..."
                className="w-full border-0 border-b border-warm-stone/40 bg-transparent px-0 py-3 font-body text-soft-charcoal placeholder:text-warm-stone/50 focus:border-faded-terracotta focus:outline-none"
              />
            </div>

            <div>
              <label className="mb-2 block font-serif text-sm tracking-wider uppercase text-warm-stone">
                Select
              </label>
              <select className="w-full border-0 border-b border-warm-stone/40 bg-transparent px-0 py-3 font-body text-soft-charcoal focus:border-faded-terracotta focus:outline-none">
                <option>Option One</option>
                <option>Option Two</option>
                <option>Option Three</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block font-serif text-sm tracking-wider uppercase text-warm-stone">
                Textarea
              </label>
              <textarea
                rows={3}
                placeholder="Write something..."
                className="w-full resize-none border-0 border-b border-warm-stone/40 bg-transparent px-0 py-3 font-body text-soft-charcoal placeholder:text-warm-stone/50 focus:border-faded-terracotta focus:outline-none"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
